import { customLog } from 'helpers/customLog';
import { useCouponsStore } from 'stores/coupon.store';
import { useCustomerStore } from 'stores/customer.store';
import { useCartStore } from 'stores/order.store';
import { useProductStore } from 'stores/product.store';

import { useEffect } from 'react';

import { BOX_DEVICES, PRODUCT } from '@/lib/constants';
import { sentryService } from '@/services/index';

import { mapCampaignsToCoupons } from './productMapper';
import { fetchCTCustomerId, fetchOrderBasedOnCustomerId } from './useCustomerOrder';

/**
 * Restores the cart from the CT API when the user opens any page in a new tab
 * and the customerId is still present in localStorage (customer store is persisted).
 * Called from ContentfulRoutes so it runs regardless of the current route.
 */
export const useCartHydration = () => {
  const { customerId, setCustomerId } = useCustomerStore(state => state);
  const { addProductToCart, setPrice, clearCart, clearCTItemsPayload, isHydrated, setIsHydrated } =
    useCartStore(state => state);
  const productListLength = useProductStore(state => state.productList.length);
  const productsAddedThisSession = useCartStore(state => state.productsAddedThisSession);

  useEffect(() => {
    if (!customerId) return;
    if (isHydrated) return; // already ran this session (Zustand memory only — resets on every page load)
    if (!productListLength) return; // wait for product catalogue to load
    // Products added this session — skip getCart (user is actively building cart)
    if (productsAddedThisSession) return;
    // Read directly from sessionStorage to avoid Zustand async rehydration race —
    // Zustand persist restores state asynchronously so products.length can be 0
    // in memory even when sessionStorage already has products.
    const rawOrderStore = sessionStorage.getItem('order-store');
    const persistedProducts = rawOrderStore ? JSON.parse(rawOrderStore)?.state?.products : null;
    if (persistedProducts?.length > 0) return;
    setIsHydrated(true); // set immediately to block any concurrent re-fires
    customLog('useCartHydration', 'No products in store, restoring cart from API');

    const hydrate = async () => {
      try {
        let resolvedCustomerId = customerId;

        if (!resolvedCustomerId) {
          resolvedCustomerId = await fetchCTCustomerId('acquisition', 'web');
          setCustomerId(resolvedCustomerId);
        }

        // Read productList directly from store to avoid stale closure
        const productList = useProductStore.getState().productList;
        // category is not available during hydration (no URL params) — prices are set
        // from raw CT pricingItems in setPrice, so productCartPayload category override is not needed.
        const response: any = await fetchOrderBasedOnCustomerId(
          resolvedCustomerId,
          productList,
          undefined
        );

        if (response?.orderProducts?.length > 0) {
          // Only clear and restore when CT actually has items to restore.
          // DO NOT clear before the network call — that would wipe products a new user
          // just added (e.g. first bundle click) since CT order is empty for new users.
          clearCTItemsPayload();
          await clearCart();
          for (const item of response.orderProducts) {
            await addProductToCart(item, item.occurrenceNumber ? BOX_DEVICES : PRODUCT);
          }
          await setPrice(
            { ...response, orderProducts: response.pricingItems ?? response.orderProducts },
            undefined
          );
          // Clear CTItemsPayload so hydrated products don't trigger a CT items API call
          clearCTItemsPayload();
          if (response.campaigns?.length > 0) {
            const mapped = mapCampaignsToCoupons(response.campaigns);
            await useCouponsStore.getState().applyMultipleCouponsToStore(mapped);
          }
        }
      } catch (error) {
        sentryService.captureException(error);
      }
    };

    void hydrate();
  }, [customerId, isHydrated, productListLength, productsAddedThisSession]);
};
